/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera, ScanLine } from "lucide-react";

/* 从二维码内容 / 手输解析资产编号：支持 完整 URL（…/assets/FAxxxx）或直接编号 */
export const parseAssetId = (text) => {
  const s = String(text || "").trim();
  const m = s.match(/\/assets\/([A-Za-z0-9_\-]{3,40})/);
  if (m) return m[1];
  return /^[A-Za-z0-9_\-]{3,40}$/.test(s) ? s : "";
};

const RES_CLS = { found: "bg-emerald-600", misplaced: "bg-amber-500", unknown: "bg-red-600", disposed: "bg-slate-700", dup: "bg-slate-500" };

/* 扫码：默认跳资产卡片；传 onDecode 则为连续扫描模式（盘点），每次识别回调并在框内显示结果 */
export function AssetScanDialog({ zh, onClose, onDecode, title }) {
  const T = (z, e) => (zh ? z : e);
  const nav = useNavigate();
  const [err, setErr] = useState("");
  const [manual, setManual] = useState("");
  const [last, setLast] = useState(null);
  const doneRef = useRef(false);
  const lastCode = useRef({ code: "", at: 0 });
  const go = async (text) => {
    const id = parseAssetId(text);
    if (!id) { setErr(T("未识别到资产编号", "No asset ID recognised")); return; }
    if (onDecode) {
      const now = Date.now();
      if (lastCode.current.code === id && now - lastCode.current.at < 2500) return;
      lastCode.current = { code: id, at: now };
      try { const r = await onDecode(id); if (r) setLast({ ...r, id }); } catch (e) { setLast({ result: "unknown", label: { zh: "失败", en: "Failed" }, id, detail: e?.detailText || e.message }); }
      setManual("");
      return;
    }
    if (doneRef.current) return;
    doneRef.current = true;
    onClose();
    nav(`/assets/${id}`);
  };
  useEffect(() => {
    if (!document.getElementById("asset-cam-reader")) return undefined;
    const s = new Html5Qrcode("asset-cam-reader", { verbose: false });
    let alive = true;
    s.start({ facingMode: "environment" }, { fps: 10, qrbox: (w, h) => { const m = Math.min(w, h, 320); return { width: m * 0.8, height: m * 0.8 }; } },
      (text) => { if (alive) go(text); }, () => {}).catch((e) => { if (alive) setErr(String(e?.message || e)); });
    return () => { alive = false; try { if (s.isScanning) s.stop().then(() => s.clear()).catch(() => {}); else s.clear(); } catch { /* not started */ } };
  }, []); // eslint-disable-line
  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-3" data-testid="asset-scan-modal" data-mode={onDecode ? "continuous" : "navigate"} onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex-wrap md:flex-nowrap flex items-center justify-between px-4 py-2.5 text-white text-sm">
          <span className="inline-flex items-center gap-1.5 font-semibold"><Camera className="h-4 w-4" />{title || T("扫资产标签 → 打开卡片归还 / 分配", "Scan asset label")}</span>
          <button type="button" data-testid="asset-scan-close" onClick={onClose} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-300 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div id="asset-cam-reader" className="w-full min-h-[220px]" />
        {last && (
          <div className={`mx-3 mt-2 rounded-lg px-3 py-2 text-white text-sm ${RES_CLS[last.result] || "bg-slate-600"}`} data-testid="asset-scan-last" data-result={last.result}>
            <b>{zh ? last.label?.zh : last.label?.en}</b> · <span className="font-mono">{last.id}</span>{last.asset?.name || last.scan?.name ? ` · ${last.asset?.name || last.scan?.name}` : ""}{last.scan?.detail ? <span className="block text-xs opacity-90">{last.scan.detail}</span> : null}{last.detail && !last.scan ? <span className="block text-xs opacity-90">{last.detail}</span> : null}
            {last.summary && <span className="block text-[11px] opacity-90 mt-0.5" data-testid="asset-scan-summary">{T(`已盘 ${last.summary.found} / ${last.summary.expected} · 未盘 ${last.summary.missing} · 错放 ${last.summary.misplaced} · 未知 ${last.summary.unknown}`, `Found ${last.summary.found}/${last.summary.expected} · missing ${last.summary.missing} · misplaced ${last.summary.misplaced} · unknown ${last.summary.unknown}`)}</span>}
          </div>
        )}
        {err && <p className="px-4 pt-2 text-xs text-red-300" data-testid="asset-scan-error">{err}{T("（摄像头需 HTTPS 并允许权限；也可在下方手输编号）", " — or type the asset ID below")}</p>}
        <form className="p-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); go(manual); }}>
          <input className="flex-1 text-sm rounded-lg bg-slate-800 border border-slate-700 text-white px-3 py-2 font-mono" data-testid="asset-scan-manual-input" value={manual} onChange={(e) => setManual(e.target.value)} placeholder={T("或手输资产编号，如 FA-LA-0001", "or type asset ID")} />
          <button type="submit" data-testid="asset-scan-go" className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-3 py-2"><ScanLine className="h-4 w-4" />{onDecode ? T("盘点", "Count") : T("打开", "Open")}</button>
        </form>
      </div>
    </div>
  );
}
